import type {CreateExtArgs, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    AutoGridCalculationConfig,
    AutoGridCalculationResults,
    CompRef,
    Pair,
    Pointer,
    Rect
} from '@wix/document-services-types'
import {calculateGridByRects} from './generators/autoGrid/autoGrid'
import type {VariantsExtensionAPI} from '../variants/variants'
import {DATA_TYPES} from '../../constants/constants'

interface LayoutAPI extends ExtensionAPI {
    generators: {
        autoGrid: {
            generate(
                containerRect: Rect,
                childPointerRectPairs: Pair<CompRef, Rect>[],
                config?: AutoGridCalculationConfig
            ): AutoGridCalculationResults
        }
    }
}

interface ComponentsAPI extends ExtensionAPI {
    isPinned(pointer: Pointer): boolean
}

export interface LayoutExtensionApi extends ExtensionAPI {
    layout: LayoutAPI
    components: ComponentsAPI
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI}: CreateExtArgs): LayoutExtensionApi => {
        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: (
                            containerRect: Rect,
                            childPointerRectPairs: Pair<CompRef, Rect>[],
                            config?: AutoGridCalculationConfig
                        ) => {
                            const results = calculateGridByRects(
                                containerRect,
                                Object.fromEntries(
                                    childPointerRectPairs.map(([compPointer, rect]) => [compPointer.id, rect])
                                ),
                                config
                            )

                            const {componentsLayouts, containerLayout} = results

                            return {
                                containerLayout,
                                componentsLayouts: childPointerRectPairs.map(([compPointer]) => [
                                    compPointer,
                                    componentsLayouts[compPointer.id]
                                ])
                            }
                        }
                    }
                }
            },

            components: {
                isPinned: (compPointer: Pointer) => {
                    const layout = (extensionAPI as VariantsExtensionAPI).variants.getComponentItemConsideringVariants(
                        compPointer,
                        DATA_TYPES.layout
                    )
                    return (
                        layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
                    )
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {layout} = extensionAPI as LayoutExtensionApi

        return {
            layout: {
                generators: {
                    autoGrid: {
                        generate: layout.generators.autoGrid.generate
                    }
                }
            }
        }
    }
    return {
        name: 'layout',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
